import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { DepartTypeEnum, cachedInflightRequest } from '@nova/core';
import { cacheableGet } from '@satellite/services/httpClient';

/**
 * Joins for Smart Gate observation data on nested asset visits in spot detail.
 * Parent paths are required by Neutron CRUD join validation (exhaustJoin).
 */
export const SPOT_DETAIL_ASSET_VISIT_OBSERVATION_JOINS = [
  'spotAssignments',
  'spotAssignments.assetVisit',
  'spotAssignments.assetVisit.vehicleObservations',
  'spotAssignments.assetVisit.vehicleObservations.assetObservations'
];

export default {
  async getOneById(spotId, params, options = {}) {
    const queryString = getQueryStringFromOptions(options);

    // Generate a unique cache key
    const cacheKey = `yard/getOneById:${spotId}:${JSON.stringify(params)}:${queryString}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`yard/spot/${spotId}`, {
        params: {
          ...params,
          join: [
            'spotAssignments',
            'spotReserves',
            'spotAssignments.assetVisit',

            'spotAssignments.assetVisit.appointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,confirmationNumber',
            'spotAssignments.assetVisit.appointment.dock||id,name,doorNumber',
            'spotAssignments.assetVisit.appointment.loadType||id,name,direction',
            'spotAssignments.assetVisit.appointment.user||id,firstName,lastName,email',
            'spotAssignments.assetVisit.appointment.user.company||id,name',

            'spotAssignments.assetVisit.assetVisitEvents||eventType,createDateTime,id,assetContainerId',
            'spotAssignments.assetVisit.assetVisitEvents.assetContainer||id,code,type,createDateTime,dropAppointmentId,pickupAppointmentId,notes,state',

            'spotAssignments.assetVisit.assetContainers||id,code,type,createDateTime,dropAppointmentId,pickupAppointmentId,notes,state',
            'spotAssignments.assetVisit.assetContainers.assetContainerEvents||id,createDateTime,eventType',

            'spotAssignments.assetContainer||id,code,type,createDateTime,dropAppointmentId,pickupAppointmentId,notes,state',
            'spotAssignments.assetContainer.assetContainerEvents||id,createDateTime,eventType',

            'spotAssignments.assetContainer.dropAppointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,confirmationNumber',
            'spotAssignments.assetContainer.dropAppointment.dock||id,name,doorNumber',
            'spotAssignments.assetContainer.dropAppointment.user||id,firstName,lastName,email',
            'spotAssignments.assetContainer.dropAppointment.user.company||id,name',
            'spotAssignments.assetContainer.dropAppointment.loadType||id,name,direction',

            'spotAssignments.assetContainer.pickupAppointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,confirmationNumber',
            'spotAssignments.assetContainer.pickupAppointment.dock||id,name,doorNumber',
            'spotAssignments.assetContainer.pickupAppointment.user||id,firstName,lastName,email',
            'spotAssignments.assetContainer.pickupAppointment.user.company||id,name',
            'spotAssignments.assetContainer.pickupAppointment.loadType||id,name,direction',

            'spotAssignments.assetVisit.assetContainers.dropAppointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,confirmationNumber',
            'spotAssignments.assetVisit.assetContainers.dropAppointment.dock||id,name,doorNumber',
            'spotAssignments.assetVisit.assetContainers.dropAppointment.user||id,firstName,lastName,email',
            'spotAssignments.assetVisit.assetContainers.dropAppointment.user.company||id,name',
            'spotAssignments.assetVisit.assetContainers.dropAppointment.loadType||id,name,direction',

            'spotAssignments.assetVisit.assetContainers.pickupAppointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,confirmationNumber',
            'spotAssignments.assetVisit.assetContainers.pickupAppointment.dock||id,name,doorNumber',
            'spotAssignments.assetVisit.assetContainers.pickupAppointment.user||id,firstName,lastName,email',
            'spotAssignments.assetVisit.assetContainers.pickupAppointment.user.company||id,name',
            'spotAssignments.assetVisit.assetContainers.pickupAppointment.loadType||id,name,direction',

            'spotAssignments.assetVisit.vehicleObservations',
            'spotAssignments.assetVisit.vehicleObservations.assetObservations'
          ]
        }
      });
      return getResponse(response, options);
    });
  },
  async getManyByWarehouseId(warehouseId, params = {}, options = {}) {
    const queryString = getQueryStringFromOptions(options);
    const fullParams = {
      ...params,
      s: {
        ...(params?.s ?? {}),
        warehouseId
      }
    };

    // Generate a unique cache key
    const cacheKey = `yard/spot:${warehouseId}:${JSON.stringify(params)}:${queryString}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`yard/spot?${queryString}`, {
        params: fullParams
      });

      return getResponse(response, options);
    });
  },
  async create(data, options = {}) {
    const response = await axios.post('yard/spot', data);
    return getResponse(response, options);
  },
  async createMany(data, options = {}) {
    const response = await axios.post('yard/spot/generate', data);
    return getResponse(response, options);
  },
  async close(spotId, data, options = {}) {
    const response = await axios.patch(`yard/spot/${spotId}/close`, data);
    return getResponse(response, options);
  },
  async open(spotId, data, options = {}) {
    const response = await axios.patch(`yard/spot/${spotId}/open`, data);
    return getResponse(response, options);
  },
  async delete(spotId, options = {}) {
    const response = await axios.delete(`yard/spot/${spotId}`);
    return getResponse(response, options);
  },
  async deleteMany(spotIds, options = {}) {
    const response = await axios.post('yard/spot/delete/bulk', { spotIds });
    return getResponse(response, options);
  },
  async update(spotId, data, options = {}) {
    const response = await axios.patch(`yard/spot/${spotId}`, data);
    return getResponse(response, options);
  },
  async createSpotAssignment(data, options = {}) {
    const response = await axios.post('yard/spot-assignment', data);
    return getResponse(response, options);
  },
  async deleteSpotAssignment(spotAssignmentId, options = {}) {
    const response = await axios.delete(`yard/spot-assignment/${spotAssignmentId}`);
    return getResponse(response, options);
  },
  async departSpotAssignment(spotAssignmentId, departType = DepartTypeEnum.All, options = {}) {
    const response = await axios.patch(`yard/spot-assignment/${spotAssignmentId}/depart`, {
      departType
    });
    return getResponse(response, options);
  },
  async reassignSpotAssignment(spotAssignmentId, data, options = {}) {
    const response = await axios.patch(`yard/spot-assignment/${spotAssignmentId}/reassign`, data);
    return getResponse(response, options);
  },
  async updateSpotAssignment(spotAssignmentId, data, options = {}) {
    delete data.spotId;
    const response = await axios.patch(`yard/spot-assignment/${spotAssignmentId}`, data);
    return getResponse(response, options);
  },
  async getEvents(spotId, options = {}) {
    const cacheKey = `yard/spot/${spotId}/events`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`yard/spot/${spotId}/events`);

      return getResponse(response, options);
    });
  },
  /**
   * Fetch spot events with improved API contract.
   *
   * NOTE: API currently exposes `GET /yard/spot/:id/events` (list). Despite the name,
   * this method returns the full events list; callers can filter for the event they need.
   *
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * @param {string} spotId - spot id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getEventV2(
    spotId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `yard/spot/${spotId}/events`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  /**
   * Get spot details with improved API contract
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * @param {string} spotId - spot id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getSpotDetails(
    spotId,
    { query = {}, getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const queryString = getQueryStringFromOptions({
      ...query,
      joins: [...SPOT_DETAIL_ASSET_VISIT_OBSERVATION_JOINS, ...(query.joins ?? [])]
    });
    const url = `yard/spot/${spotId}?${queryString}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  /**
   * Fetch spot assignments for a given spot.
   * Can be used independently or alongside getSpotDetails().
   * Uses the spot-assignment endpoint filtered by spotId.
   * Returns assignments with full assetVisit and assetContainer objects.
   *
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   * @param {string} spotId - spot id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getSpotAssignments(
    spotId,
    { query = {}, getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const queryString = getQueryStringFromOptions(query);
    const url = `yard/spot-assignment?${queryString}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      params: {
        s: { spotId },
        // NOTE: In practice this endpoint seems to return only IDs for relations,
        // even when joins are requested. For the PoC, we intentionally fetch
        // only IDs here and let dedicated presenters fetch the related entities.
        ...requestConfig.params
      },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  }
};
